import { useNavigate } from "react-router-dom";
import { Link2, UserPlus, ShieldCheck, IdCard, BadgeCheck } from "lucide-react";

/* Route to your ABHA / patient registration page */
const ROUTES = {
  PATIENTREGISTRATION: "/patients/registration",
};

/* ── Doxzon palette (same tokens as AbhaRegistration.jsx) ───────────── */
const C = {
  navy: "#1e4d6b",
  navyDark: "#0d2d45",
  teal: "#0ba88a",
  tealLight: "#3dd6c8",
  ink: "#1e3a5f",
  sub: "#7c8ba1",
  border: "#e4e9ee",
  page: "#eef2f6",
  card: "#ffffff",
  grad: "linear-gradient(135deg, #0d2d45 0%, #0ba88a 55%, #3dd6c8 100%)",
};

const FEATURES = [
  {
    icon: Link2,
    title: "Instant ABHA Linking",
    desc: "Search and link a patient's existing ABHA number or address in seconds.",
  },
  {
    icon: UserPlus,
    title: "Create ABHA On the Spot",
    desc: "No existing ABHA? Generate one instantly during patient registration via OTP verification.",
  },
  {
    icon: ShieldCheck,
    title: "OTP-Secured Verification",
    desc: "Every link and creation is authenticated with mobile OTP for complete data security.",
  },
  {
    icon: IdCard,
    title: "Digital Health ID Card",
    desc: "Auto-generated ABHA card with patient details, ready to view and share.",
  },
  {
    icon: BadgeCheck,
    title: "ABDM Compliant",
    desc: "Fully aligned with National Health Authority standards for interoperable health records.",
  },
];

export default function AbhaFeatureSection() {
  const navigate = useNavigate();

  return (
    <section
      style={{
        background: C.page,
        padding: "72px 20px",
        fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif",
      }}
    >
      <div style={{ maxWidth: "1080px", margin: "0 auto" }}>
        {/* Eyebrow */}
        <div style={{ display: "flex", justifyContent: "center", marginBottom: "18px" }}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              background: "#eafcf8",
              border: "1px solid #bdeee4",
              color: C.teal,
              fontSize: "11.5px",
              fontWeight: 700,
              letterSpacing: "0.04em",
              borderRadius: "9999px",
              padding: "6px 14px",
            }}
          >
            <ShieldCheck size={13} /> ABDM CERTIFIED
          </span>
        </div>

        {/* Headline */}
        <h2
          style={{
            textAlign: "center",
            fontSize: "clamp(26px, 3.4vw, 38px)",
            fontWeight: 800,
            color: C.ink,
            margin: "0 auto 14px",
            maxWidth: "700px",
            lineHeight: 1.25,
          }}
        >
          ABHA Integrated — Digital Health Records, Simplified
        </h2>

        {/* Sub-headline */}
        <p
          style={{
            textAlign: "center",
            fontSize: "15px",
            color: C.sub,
            maxWidth: "620px",
            margin: "0 auto 48px",
            lineHeight: 1.7,
          }}
        >
          Doxzon is seamlessly integrated with India's Ayushman Bharat Health Account (ABHA),
          enabling secure, paperless patient onboarding under the National Health Authority's
          ABDM framework.
        </p>

        {/* Feature grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: "18px",
            marginBottom: "44px",
          }}
        >
          {FEATURES.map(({ icon: Icon, title, desc }) => (
            <div
              key={title}
              style={{
                background: C.card,
                border: `1px solid ${C.border}`,
                borderRadius: "14px",
                padding: "22px 20px",
              }}
            >
              <span
                style={{
                  width: "38px",
                  height: "38px",
                  borderRadius: "10px",
                  background: C.grad,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "14px",
                }}
              >
                <Icon size={18} color="#fff" />
              </span>
              <p style={{ margin: "0 0 6px", fontSize: "14.5px", fontWeight: 700, color: C.ink }}>
                {title}
              </p>
              <p style={{ margin: 0, fontSize: "12.5px", color: C.sub, lineHeight: 1.6 }}>
                {desc}
              </p>
            </div>
          ))}
        </div>

        {/* Closing banner */}
        <div
          style={{
            background: C.grad,
            borderRadius: "16px",
            padding: "28px 32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "16px",
          }}
        >
          <p style={{ margin: 0, fontSize: "15px", fontWeight: 700, color: "#fff", maxWidth: "480px", lineHeight: 1.5 }}>
            One patient record. One health ID. Fully connected to India's digital health
            ecosystem — powered by Doxzon.
          </p>
          <button
            onClick={() => navigate(ROUTES.PATIENTREGISTRATION)}
            style={{
              flexShrink: 0,
              padding: "12px 24px",
              borderRadius: "9999px",
              border: "none",
              background: "#fff",
              color: C.navyDark,
              fontWeight: 700,
              fontSize: "13.5px",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Explore ABHA Integration →
          </button>
        </div>
      </div>
    </section>
  );
}